import React from "react";

import {
  Participant,
  SearchParticipantProps,
  ShowFileDialogProps,
} from "../interfaces/interfaces";
import axios from "axios";
import { User, AppContext } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";
import checkPermissionAdmin from "../../../functions/checkPermissionAdmin";
import IPagination from "../../../components/pagination/interface/iPagination";

export const SearchParticipantContext = React.createContext<any>({});

const SearchParticipantProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [paginationSettings, setPaginationSettings] =
    React.useState<IPagination>({
      page: 1,
      rowsPerPage: 5,
    });

  const [searchParameters, setSearchParameters] =
    React.useState<SearchParticipantProps>({
      participantName: "",
      show: {
        active: true,
        inactive: false,
      },
      loading: false,
      data: null,
    });

  const [showFileDialog, setShowFileDialog] =
    React.useState<ShowFileDialogProps>({
      open: false,
      participant: null,
    });

  const startSearch = React.useCallback(async (): Promise<void> => {
    setSearchParameters({
      ...searchParameters,
      loading: true,
    });

    setPaginationSettings({
      page: 1,
      rowsPerPage: 5,
    });

    const endpoint: string =
      checkPermissionAdmin(user) || user.isSpeedCoach === 1
        ? endpoints.admin.searchParticipantFull
        : endpoints.coach.searchParticipant;

    await axios
      .post(endpoint, {
        sessionId: user.sessionId,
        searchString: searchParameters.participantName,
      })
      .then((response) => {
        checkSession(response);

        // formatting the data for better view
        if (response.data.data.length > 0) {
          response.data.data.forEach((participant: Participant) => {
            // adds a collapse property to each participant
            participant.collapse = false;

            // sorts the appointments by date
            participant.appointments.data.sort((a: any, b: any) => {
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            });
          });
        }

        setSearchParameters({
          ...searchParameters,
          data: response.data.data,
          loading: false,
        });
      })
      .catch((error) => {
        setSearchParameters({
          ...searchParameters,
          loading: false,
        });
        console.log(error);
      });
  }, [checkSession, searchParameters, user]);

  const showParticipantDetails = React.useCallback(
    (participant: Participant) => {
      const oldParticipants = searchParameters.data;

      if (oldParticipants !== null) {
        oldParticipants.forEach((oldParticipant) => {
          if (oldParticipant.participantId === participant.participantId) {
            oldParticipant.collapse = !oldParticipant.collapse;
          }
        });

        setSearchParameters({
          ...searchParameters,
          data: oldParticipants,
        });
      }
    },
    [searchParameters]
  );

  const filteredResults: Participant[] | undefined =
    searchParameters.data?.filter((participant: Participant) => {
      return (
        (searchParameters.show.active && participant.status === 0) ||
        (searchParameters.show.inactive && participant.status === 1)
      );
    });

  const value: {} = React.useMemo(
    () => ({
      searchParameters,
      setSearchParameters,
      startSearch,
      showFileDialog,
      setShowFileDialog,
      showParticipantDetails,
      paginationSettings,
      setPaginationSettings,
      filteredResults,
    }),
    [
      searchParameters,
      startSearch,
      showFileDialog,
      showParticipantDetails,
      paginationSettings,
      filteredResults,
    ]
  );

  return (
    <SearchParticipantContext.Provider value={value}>
      {children}
    </SearchParticipantContext.Provider>
  );
};

export default SearchParticipantProvider;
